import { ref, onMounted, onUnmounted, computed } from "vue";

/**Composable для отслеживания смещения мыши. */
export function useMouseTracking() {
  /**Стартовая позиция мыши */
  const startPosition = ref({ x: 0, y: 0 });
  /**Текущая позиция мыши */
  const currentPosition = ref({ x: 0, y: 0 });

  /**Отслеживается ли смещение мыши */
  const isTracking = ref(false);

  /**Функция для обновления текущей позиции мыши или касания */
  const updateMousePosition = (event: any) => {
    if (event instanceof MouseEvent) {
      currentPosition.value = { x: event.clientX, y: event.clientY };
    } else if (event instanceof TouchEvent) {
      const touch = event.touches[0]; // Первый палец, если есть несколько
      currentPosition.value = { x: touch.clientX, y: touch.clientY };
    }
  };

  /**
   * Начать отслеживание
   * @param event
   */
  const start = (event: MouseEvent | TouchEvent) => {
    isTracking.value = true;

    // Запоминаем стартовую позицию мыши или касания
    if (event instanceof MouseEvent) {
      startPosition.value = { x: event.clientX, y: event.clientY };
    } else if (event instanceof TouchEvent) {
      const touch = event.touches[0];
      startPosition.value = { x: touch.clientX, y: touch.clientY };
    }

    // Устанавливаем текущую позицию как стартовую
    currentPosition.value = { ...startPosition.value };
  };

  /**Завершить отслеживание */
  const stop = () => {
    isTracking.value = false;
    startPosition.value = { x: 0, y: 0 };
  };

  /**Подписаться/отписаться от событий */
  const toggleEvents = (isSubscribe: boolean) => {
    const fnName = isSubscribe ? "addEventListener" : "removeEventListener";

    window[fnName]("mousemove", updateMousePosition);
    window[fnName]("touchmove", updateMousePosition);
  };

  onMounted(() => {
    toggleEvents(true);
  });

  onUnmounted(() => {
    toggleEvents(false);
  });

  /**
   * Реактивное свойство для получения смещения мыши относительно стартовой позиции.
   * Возвращает разницу между текущей позицией и стартовой.
   */
  const offset = computed(() => ({
    x: isTracking.value ? currentPosition.value.x - startPosition.value.x : 0,
    y: isTracking.value ? currentPosition.value.y - startPosition.value.y : 0,
  }));

  return {
    /** Стартовая позиция мыши `{ x, y }`, фиксируется при старте отслеживания */
    startPosition,
    /** Текущая позиция мыши `{ x, y }`, обновляется при движении мыши */
    currentPosition,
    /** Смещение мыши относительно стартовой позиции. */
    offset,
    /**Отслеживается ли смещение мыши */
    isTracking,
    /**Начать отслеживание */
    start,
    /**Завершить отслеживание */
    stop,
  };
}
